import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import api from '../../api/client.js';

const FUNCS = [
  { key: 'all',         label: 'All' },
  { key: 'bdm',         label: 'BDM' },
  { key: 'equipment',   label: 'Equipment Sales' },
  { key: 'aftermarket', label: 'Aftermarket' },
];
const FUNC_LABEL = { bdm: 'BDM', aftermarket: 'Aftermarket', equipment: 'Equipment' };
const FUNC_BADGE = {
  bdm: 'bg-navy/10 text-navy',
  aftermarket: 'bg-amber-100 text-amber-900',
  equipment: 'bg-emerald-100 text-emerald-900',
};
const STATUSES = [
  { key: '',         label: 'Any status' },
  { key: 'imported', label: 'Imported' },
  { key: 'no_note',  label: 'No note' },
  { key: 'error',    label: 'Error' },
];
const STATUS_BADGE = {
  imported: { cls: 'bg-emerald-100 text-emerald-900', label: '✓ Imported' },
  no_note:  { cls: 'bg-panel text-muted',             label: 'No note' },
  error:    { cls: 'bg-crimson/10 text-crimson',      label: '✕ Error' },
};
const PAGE = 50;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function IngestedEmails() {
  const [func, setFunc] = useState('all');
  const [status, setStatus] = useState('');
  const [repId, setRepId] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const reps = useQuery({
    queryKey: ['ingested-reps'],
    queryFn: () => api.get('/admin/call-reports/ingested/reps').then(r => r.data.data),
    staleTime: 5 * 60 * 1000,
  });

  const params = new URLSearchParams();
  if (func !== 'all') params.set('function', func);
  if (status) params.set('status', status);
  if (repId) params.set('user_id', repId);
  if (q.trim()) params.set('q', q.trim());
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  params.set('limit', String(PAGE));
  params.set('offset', String(offset));

  const list = useQuery({
    queryKey: ['ingested-emails', params.toString()],
    queryFn: () => api.get(`/admin/call-reports/ingested?${params.toString()}`).then(r => r.data),
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.data || [];
  const total = list.data?.total ?? 0;

  // Reset paging whenever a filter changes
  function setFilter(setter) {
    return (v) => { setter(v); setOffset(0); setExpanded(null); };
  }

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Management</div>
        <h1 className="text-2xl">Ingested Emails</h1>
        <div className="rule-crimson" />
        <p className="text-sm text-muted">
          Call-report emails pulled from rep mailboxes and turned into CRM notes — by sales function.
        </p>
      </header>

      {/* Function tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {FUNCS.map(f => (
          <button key={f.key} onClick={() => setFilter(setFunc)(f.key)}
            className={clsx('btn px-2.5 py-1 text-xs', func === f.key ? 'bg-crimson text-white' : 'bg-panel text-navy')}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-end">
        <div>
          <label className="label text-xs">Search subject</label>
          <input value={q} onChange={e => setFilter(setQ)(e.target.value)}
            placeholder="subject contains…" className="input text-sm" />
        </div>
        <div>
          <label className="label text-xs">Rep</label>
          <select value={repId} onChange={e => setFilter(setRepId)(e.target.value)} className="input text-sm">
            <option value="">All reps</option>
            {(reps.data || []).map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label text-xs">Status</label>
          <select value={status} onChange={e => setFilter(setStatus)(e.target.value)} className="input text-sm">
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label text-xs">From</label>
          <input type="date" value={from} onChange={e => setFilter(setFrom)(e.target.value)} className="input text-sm" />
        </div>
        <div>
          <label className="label text-xs">To</label>
          <input type="date" value={to} onChange={e => setFilter(setTo)(e.target.value)} className="input text-sm" />
        </div>
      </div>

      {list.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {list.isError && <div className="text-crimson text-sm">Failed to load.</div>}

      {list.data && (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] text-muted border-b border-navy-100 text-left">
                  <th className="p-2 font-medium">Received</th>
                  <th className="p-2 font-medium">Rep</th>
                  <th className="p-2 font-medium">Subject</th>
                  <th className="p-2 font-medium text-center">Customers</th>
                  <th className="p-2 font-medium">Status</th>
                  <th className="p-2 font-medium">Processed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const isOpen = expanded === r.id;
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.no_note;
                  return (
                    <RowGroup key={r.id} r={r} isOpen={isOpen} badge={badge}
                      onToggle={() => setExpanded(isOpen ? null : r.id)} />
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted text-sm">No ingested emails match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-muted">
            <div>{total === 0 ? 'No results' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}</div>
            <div className="flex gap-2">
              <button className="btn bg-panel text-navy px-3 py-1 disabled:opacity-40"
                disabled={offset === 0} onClick={() => { setOffset(o => Math.max(0, o - PAGE)); setExpanded(null); }}>
                ← Prev
              </button>
              <button className="btn bg-panel text-navy px-3 py-1 disabled:opacity-40"
                disabled={offset + PAGE >= total} onClick={() => { setOffset(o => o + PAGE); setExpanded(null); }}>
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      <div className="text-[11px] text-muted">
        Click a row to preview the customers, follow-ups, and opportunities the email produced.
        <b> Imported</b> = at least one note created · <b>No note</b> = parsed but no customer/interaction found · <b>Error</b> = parsing failed.
      </div>
    </div>
  );
}

function RowGroup({ r, isOpen, badge, onToggle }) {
  return (
    <>
      <tr onClick={onToggle}
          className={clsx('border-b last:border-0 cursor-pointer hover:bg-panel', isOpen && 'bg-panel')}>
        <td className="p-2 whitespace-nowrap">{fmtDate(r.received_at)}</td>
        <td className="p-2 whitespace-nowrap">
          <div className="font-medium text-navy">{r.user_name}</div>
          {r.sales_function && (
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', FUNC_BADGE[r.sales_function])}>
              {FUNC_LABEL[r.sales_function]}
            </span>
          )}
        </td>
        <td className="p-2">
          <span className="text-navy">{r.subject || '(no subject)'}</span>
          {r.note_customer_name && <div className="text-[10px] text-muted">→ {r.note_customer_name}</div>}
        </td>
        <td className="p-2 text-center tabular-nums">{r.customer_count || '—'}</td>
        <td className="p-2 whitespace-nowrap">
          <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', badge.cls)}>{badge.label}</span>
        </td>
        <td className="p-2 whitespace-nowrap text-muted">{fmtDate(r.processed_at)}</td>
      </tr>
      {isOpen && (
        <tr className="bg-panel/50">
          <td colSpan={6} className="p-3">
            <Preview r={r} />
          </td>
        </tr>
      )}
    </>
  );
}

function Preview({ r }) {
  if (r.error) {
    return <div className="text-sm text-crimson">Parsing failed: {r.error}</div>;
  }
  const customers = r.raw_extraction?.customers || [];
  if (customers.length === 0) {
    return <div className="text-sm text-muted">No customer interactions were extracted from this email.</div>;
  }
  return (
    <div className="space-y-3">
      {r.note_id && (
        <Link to={`/notes/${r.note_id}`} className="text-xs text-crimson hover:underline">Open the created note →</Link>
      )}
      {customers.map((c, i) => (
        <div key={i} className="bg-white rounded border border-navy-100 p-3 space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-navy">{c.name || 'Unknown customer'}</span>
            {c.touch_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-panel text-muted">{c.touch_type}</span>}
            {c.contact_person && <span className="text-xs text-muted">· {c.contact_person}</span>}
          </div>
          {c.summary && <div className="text-sm text-navy/80">{c.summary}</div>}
          {(c.follow_ups || []).length > 0 && (
            <div className="text-xs">
              <span className="text-muted">Follow-ups: </span>
              {c.follow_ups.map((f, j) => (
                <span key={j} className="inline-block mr-2">⏱ {f.title}{f.urgency ? ` (${f.urgency})` : ''}</span>
              ))}
            </div>
          )}
          {(c.opportunities || []).length > 0 && (
            <div className="text-xs">
              <span className="text-muted">Opportunities: </span>
              {c.opportunities.map((o, j) => (
                <span key={j} className="inline-block mr-2">◢ {o.name}{o.domain ? ` · ${o.domain}` : ''}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
